import React, { useCallback, useState, useMemo } from 'react'
import {
  Select,
  Text,
  Label,
  XStack,
  Dialog,
  YStack,
  View,
  useMedia,
  Spinner,
  Button,
} from 'tamagui'
import { CoverImage } from '@components/CoverImage'
import { HeroIcon } from '@components/HeroIcon'
import { Modal } from './Modal'
import { IOScrollView, InView } from '@components/IntersectionObserver'

type School = {
  id: string
  name: string
  organizationId: number
  url: string
  logo?: string
}

interface ISchoolSelectProps
  extends Omit<React.ComponentProps<typeof Select>, 'value' | 'onValueChange'> {
  label?: string
  options: School[]
  description?: string
  value?: School
  onValueChange?: (value: School) => void
  onLoadMore?: () => void
  isLoadingMore?: boolean
  hasMoreItems?: boolean
  onOpenSchoolSelect?: () => void
}

export function SchoolSelect({
  label,
  options,
  description,
  onValueChange,
  onLoadMore,
  isLoadingMore,
  hasMoreItems = false,
  onOpenSchoolSelect,
  ...props
}: ISchoolSelectProps) {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState<School | undefined>(props.value)
  const media = useMedia()

  const handleCloseModal = useCallback(() => {
    setOpen(false)
  }, [])

  const getTabletItemWidth = useMemo(() => {
    const remainingItems = options.length % 3

    return (index: number) => {
      const isLastItem = index === options.length - 1

      if (!isLastItem) return '31%'
      if (remainingItems === 1) return '100%'
      if (remainingItems === 2) return '65%'
      return '31%'
    }
  }, [options.length])

  const getMobileItemWidth = useMemo(() => {
    const isOdd = options.length % 2 !== 0

    return (index: number) => {
      const isLastItem = index === options.length - 1
      if (isLastItem && isOdd) return '100%'
      return '48%'
    }
  }, [options.length])

  const getItemWidth = useCallback(
    (index: number) => {
      return media.gtSm ? getTabletItemWidth(index) : getMobileItemWidth(index)
    },
    [media.gtSm, getTabletItemWidth, getMobileItemWidth],
  )

  const handleValueChange = useCallback(
    (selectedValue: School) => {
      setValue(selectedValue)
      onValueChange?.(selectedValue)
      handleCloseModal()
    },
    [onValueChange, handleCloseModal],
  )

  const renderFooter = useCallback(() => {
    if (!isLoadingMore) return null

    return (
      <View paddingVertical="$size.spacing-xs" alignItems="center">
        <Spinner size="large" color="$spinner-strong" mb="$size.spacing-xs" />
      </View>
    )
  }, [isLoadingMore])

  const renderItem = useCallback(
    ({ item: option }: { item: School }) => {
      return (
        <YStack
          backgroundColor={
            value?.id === option.id ? '$accent-subtle' : '$bg-surface-raised'
          }
          onPress={() => handleValueChange(option)}
          padding="$size.spacing-xxs"
          borderRadius="$border-radius-md"
          gap="$size.spacing-xxxs"
          alignItems="center"
          width="100%"
        >
          {option.logo ? (
            <CoverImage
              uri={option.logo}
              width="100%"
              height={71}
              objectFit="contain"
              alt={option.name}
            />
          ) : (
            <View height={71} justifyContent="center" alignItems="center">
              <HeroIcon icon={['fal', 'books']} size={48} color="$text-muted" />
            </View>
          )}

          <View width="100%" height={1} backgroundColor="$border-default" />

          <Text
            ff="$mulishBold"
            color="$text-primary"
            fontSize="$font-size-xs"
            fontWeight={700}
            whiteSpace="break-spaces"
            textAlign="center"
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {option.name}
          </Text>
        </YStack>
      )
    },
    [value, handleValueChange],
  )

  const handleOpenModal = useCallback(() => {
    setOpen(true)
    onOpenSchoolSelect?.()
  }, [onOpenSchoolSelect])

  return (
    <YStack gap="$size.spacing-xxxs">
      <Label
        color="$text-primary"
        ff="$mulishRegular"
        fontSize="$font-size-xxs"
        lineHeight={21}
      >
        {label}
      </Label>
      <XStack
        width="100%"
        backgroundColor="$bg-input"
        borderWidth="$borderWidth.border-width-hairline"
        borderColor="$border-strong"
        borderRadius="$border-radius-md"
        paddingVertical="$size.spacing-xxxs"
        paddingHorizontal="$size.spacing-xs"
        height={48}
        gap="$size.spacing-xxxs"
        justifyContent="space-between"
        alignItems="center"
        onPress={handleOpenModal}
      >
        <XStack flex={1} alignItems="center" gap="$size.spacing-xxxs">
          <YStack width={16}>
            <HeroIcon icon={['fal', 'globe']} size={16} color="$icon-primary" />
          </YStack>
          <Text
            flex={1}
            color="$text-primary"
            ff="$mulishRegular"
            fontSize="$font-size-xs"
            numberOfLines={1}
          >
            {props.value?.name || 'Selecione sua escola'}
          </Text>
        </XStack>

        <YStack width={12}>
          <HeroIcon
            icon={['fas', 'chevron-down']}
            size={12}
            color="$icon-primary"
          />
        </YStack>
      </XStack>

      <Modal
        open={open}
        onClose={handleCloseModal}
        snapPoints={[80, 100]}
        backdropOpacity={0.9}
        disableDrag
        shouldAdapt={false}
        whenShouldAdapt={media.gtSm ? 'md' : 'sm'}
      >
        <Button
          justifyContent="center"
          alignItems="center"
          position="absolute"
          top={10}
          right={5}
          h={32}
          w={32}
          unstyled
          backgroundColor="transparent"
          onPress={handleCloseModal}
        >
          <HeroIcon icon={['fal', 'xmark']} size={20} color="$icon-primary" />
        </Button>
        <Dialog.Title
          color="$text-primary"
          ff="$mulishBold"
          fontSize="$font-size-sm"
          paddingHorizontal="$size.spacing-xs"
          marginTop="$size.spacing-md"
          textAlign={media.gtSm ? 'center' : 'left'}
        >
          {label}
        </Dialog.Title>
        {description && (
          <Dialog.Description
            color="$text-secondary"
            ff="$mulishRegular"
            fontSize="$font-size-xxs"
            paddingHorizontal="$size.spacing-xs"
            marginTop="$size.spacing-xs"
            textAlign={media.gtSm ? 'center' : 'left'}
          >
            {description}
          </Dialog.Description>
        )}

        <IOScrollView>
          <YStack
            key="school-select"
            paddingVertical="$size.spacing-md"
            paddingHorizontal={media.gtSm ? '10%' : '$spacing-inset-xs'}
          >
            <XStack
              flexWrap="wrap"
              rowGap="$spacing-inset-xs"
              justifyContent="space-between"
            >
              {options.map((option, index) => {
                const width = getItemWidth(index)

                if (index === options.length - 1) {
                  return (
                    <InView
                      key={option.id}
                      onChange={(inView: boolean) =>
                        inView && hasMoreItems && onLoadMore?.()
                      }
                      threshold={0.5}
                      style={{
                        width,
                      }}
                    >
                      {renderItem({ item: option })}
                    </InView>
                  )
                }

                return (
                  <View key={option.id} width={width}>
                    {renderItem({ item: option })}
                  </View>
                )
              })}
            </XStack>
            {renderFooter()}
          </YStack>
        </IOScrollView>
      </Modal>
    </YStack>
  )
}
